import {
  WSSelectOption,
  WSSelectSearch,
  WSSelectSearchProps
} from "@wingspanhq/fe-component-library";
import {
  IPayerEngagementListResponse,
  IPayerResponse
} from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useCallback, useMemo, useState } from "react";
import { useQueryPayerRows } from "../../../../query/search/payer/queries/useQueryPayerRows";
import { getPayerEngagements } from "../../../../services/payerEngagements";
import { PayerRowsQuery } from "../../../../services/search";
import { selectorIsEngagementsActiveForPayer } from "../../../Payees/selectors/selectorIsEngagementsActiveForPayer";
import {
  selectorPayerEngagementNameTmp,
  selectorPayerEngagementsTmp
} from "../../../Payees/selectors/selectorPayerEngagementsTmp";
import { selectorPayerPayeeEngagementName } from "../../../Payees/selectors/selectorPayerPayeeEngagementName";
import { selectorPayerPayeeEngagements } from "../../../Payees/selectors/selectorPayerPayeeEngagements";
import { useAddClientModal } from "../../components/AddClientModal/useAddClientModal";

type Props = Omit<WSSelectSearchProps, "options"> & {
  defaultSearchText?: string;
  onAddNewPayer: (
    payer: IPayerResponse,
    engagements: IPayerEngagementListResponse
  ) => void;
  baseRequest?: PayerRowsQuery;
};

export const SelectPayerEngagement: React.FC<Props> = ({
  defaultSearchText,
  onAddNewPayer,
  baseRequest,
  ...otherProps
}) => {
  const [searchText, setSearchText] = useState(defaultSearchText);
  const queryPayerRows = useQueryPayerRows({
    ...baseRequest,
    filter: {
      ...baseRequest?.filter,
      searchString: searchText || undefined
    }
  });

  const onReachMenuEnd = useCallback(() => {
    if (queryPayerRows.hasNextPage) {
      queryPayerRows.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPayerRows.hasNextPage]);

  const modalAddClient = useAddClientModal();

  const options = useMemo(() => {
    const options: WSSelectOption[] = [];

    (queryPayerRows.data || []).forEach(payer => {
      if (!payer.user) {
        return;
      }

      const payerNames = wsName({
        user: payer.user,
        member: payer.member,
        payeeOwnedData: payer.payeeOwnedData
      });

      const name = payerNames.getResolvedName();
      const secondaryName = payerNames.getResolvedSecondaryName();

      const isEngagementsActiveForPayer = selectorIsEngagementsActiveForPayer(
        payer.engagements
      );

      let filteredEngagements = isEngagementsActiveForPayer
        ? selectorPayerPayeeEngagements(payer.engagements, true)
        : selectorPayerEngagementsTmp(payer.engagements, false, false);

      // Filter by isWorkLoggingEnabled if the filter is set in baseRequest
      const workLoggingFilter =
        baseRequest?.filter?.["engagements.isWorkLoggingEnabled"];
      if (workLoggingFilter !== undefined) {
        filteredEngagements = filteredEngagements.filter(e => {
          // Handle both boolean and string values from API
          const isEnabled =
            e.isWorkLoggingEnabled === true ||
            e.isWorkLoggingEnabled === "true";
          return workLoggingFilter ? isEnabled : !isEnabled;
        });
      }

      filteredEngagements.forEach((e, index, all) => {
        options.push({
          value: payer.payerId + "/" + e.payerPayeeEngagementId,
          label: [
            name,
            isEngagementsActiveForPayer
              ? selectorPayerPayeeEngagementName(e, false)
              : all.length > 1
              ? selectorPayerEngagementNameTmp(e, true)
              : undefined
          ]
            .filter(Boolean)
            .join(" • "),
          labels: [secondaryName].filter(Boolean) as string[],
          avatar: {
            type: "text",
            text: name
          }
        });
      });
    });

    return options;
  }, [queryPayerRows.data, baseRequest?.filter]);

  return (
    <WSSelectSearch
      {...otherProps}
      // label="Payer"
      placeholder="Select an option"
      options={options}
      status={queryPayerRows.isInitialLoading ? "loading" : otherProps.status}
      externalSearchText={searchText}
      onExternalSearchChange={setSearchText}
      isListLoading={
        queryPayerRows.isFetching && !queryPayerRows.isFetchingNextPage
      }
      onReachMenuEnd={onReachMenuEnd}
      menuFooterText="Don't see a Client name? Add a new client to your client list."
      menuFooterAction={{
        label: "Add client",
        onClick: async () => {
          await modalAddClient.open({
            async onSuccess(payer) {
              if (payer) {
                const engagements = await getPayerEngagements(payer.payerId);
                onAddNewPayer(payer, engagements);
              }
            }
          });
        }
      }}
    />
  );
};
